var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ModalProvider, useModalContext } from '../context';
import { ModalAnimationState } from '../type';
import { BackDrop, Container, ModalContainer, Wrap, Wrapper } from './Modal.style';
/**
 * # Modal.Modal
 *
 * @description [vercel design modal](https://vercel.com/design/modal)
 *
 * 모달의 베이스가 되는 컴포넌트입니다.
 * 하위에 Modal의 요소들을 선언하여 사용하셔야합니다.
 *
 * @param {ModalProps} {@link ModalProps} 기본적인 modal 요소
 *
 * @example ```tsx
 * const { active, open, close } = useModal()
 *
 * return (
 * <>
 *   <TemporaryContainer>
 *     <Button onClick={open}>알럿 출력</Button>
 *   </TemporaryContainer>
 *
 *   <Modal.Modal active={active} onClickOutSide={close}>
 *    <Modal.Body>
 *      <Modal.Header>
 *        <Modal.Title>안녕하세요?</Modal.Title>
 *        <Modal.SubTitle>This is a modal</Modal.SubTitle>
 *      </Modal.Header>
 *      <Modal.Inset>
 *        <Text>Content within the inset.</Text>
 *      </Modal.Inset>
 *      <br />
 *      <Text>Content outside the inset.</Text>
 *    </Modal.Body>
 *    <Modal.Actions>
 *      <Modal.Action onClick={close}>Cancel</Modal.Action>
 *      <Modal.Action onClick={close}>Submit</Modal.Action>
 *    </Modal.Actions>
 *  </Modal.Modal>
 * </>
 * )
 * ```
 */
export var Modal = function (_a) {
    var _b;
    var children = _a.children, props = __rest(_a, ["children"]);
    return createPortal(<ModalProvider>
      <Contents {...props}>{children}</Contents>
    </ModalProvider>, (_b = document.getElementById('modal-root')) !== null && _b !== void 0 ? _b : document.body);
};
var Contents = function (props) {
    var _a = useModalContext(props), children = _a.children, uniqueId = _a.uniqueId, nextAnimationState = _a.nextAnimationState, changeAnimationState = _a.changeAnimationState, active = _a.active, animationState = _a.animationState, onClickOutSide = _a.onClickOutSide, otherProps = __rest(_a, ["children", "uniqueId", "nextAnimationState", "changeAnimationState", "active", "animationState", "onClickOutSide"]);
    var ref = useRef(null);
    useEffect(function () {
        if (active) {
            document.body.style.overflow = 'hidden';
            changeAnimationState(ModalAnimationState.FADE_IN);
        }
        else {
            if (animationState === ModalAnimationState.ALIVE) {
                document.body.style.overflow = '';
                changeAnimationState(ModalAnimationState.FADE_OUT);
            }
        }
    }, [active]);
    useEffect(function () {
        var _a;
        if (animationState === ModalAnimationState.ALIVE) {
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [animationState]);
    return (<Wrap id={uniqueId} animationState={animationState}>
      <BackDrop animationState={animationState} {...otherProps}/>
      <Container animationState={animationState} onAnimationEnd={nextAnimation}>
        <Wrapper ref={ref} tabIndex={1} role='dialog' aria-hidden aria-modal aria-labelledby='modal' onBlur={handleReset} onKeyDown={handleESCKeyDown}>
          <ModalContainer>{children}</ModalContainer>
        </Wrapper>
      </Container>
    </Wrap>);
    function nextAnimation() {
        nextAnimationState();
    }
    function handleReset() {
        onClickOutSide === null || onClickOutSide === void 0 ? void 0 : onClickOutSide();
    }
    function handleESCKeyDown(e) {
        if (e.key === 'Escape')
            handleReset();
    }
};
//# sourceMappingURL=Modal.jsx.map